import { GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

/**
 * 사용자가 선택한 단일 셀의 위치입니다.
 * rowIndex는 정렬과 필터가 반영된 현재 화면의 행 순번이고,
 * colId는 AG Grid 컬럼 정의에 설정된 고유 ID입니다.
 */
export interface GridCellPosition {
	rowIndex: number;
	colId: string;
}

/**
 * 마우스 드래그로 만들어진 사각형 선택 범위입니다.
 * anchor는 드래그를 시작한 셀이고 selected는 드래그가 끝난 셀이므로,
 * 사용자의 드래그 방향에 따라 anchor가 우측이나 아래쪽에 위치할 수도 있습니다.
 */
export interface GridCellRangeSelection {
	anchor: GridCellPosition;
	selected: GridCellPosition;
}

/** 계산 가능한 셀을 하나의 숫자 배열로 합친 후 산출한 통계 결과입니다. */
export interface GridCellCalculationSummary {
	/** 숫자로 변환하는 데 성공하여 실제 계산에 포함된 셀의 개수 */
	count: number;
	/** 계산 대상 숫자 전체의 합계 */
	sum: number;
	/** 합계를 count로 나눈 산술 평균 */
	average: number;
	/** 계산 대상 숫자 중 최솟값 */
	min: number;
	/** 계산 대상 숫자 중 최댓값 */
	max: number;
}

/** Grid Wrapper가 ref를 통해 외부에 제공하는 셀 범위 선택 제어 메서드입니다. */
export interface GridCellSelectionMethods {
	clearRange: () => void;
	getSelectedCellRange: () => GridCellRangeSelection | null;
}

/** AG Grid 기본 ref에 셀 범위 선택 메서드가 선택적으로 확장된 타입입니다. */
export type GridWithCellSelection<TData> = AgGridReact<TData> & Partial<GridCellSelectionMethods>;

/**
 * 드래그 방향과 관계없이 반복문에서 사용할 수 있도록 선택 범위를 정규화합니다.
 * 컬럼 위치는 원본 columnDefs 순서가 아니라 이동·고정·숨김 상태가 반영된
 * 현재 표시 컬럼 순서를 기준으로 계산합니다. 시작 또는 종료 컬럼이 현재 화면에
 * 표시되지 않는 경우에는 안전하게 계산을 중단할 수 있도록 null을 반환합니다.
 */
const getRangeBounds = <TData>(api: GridApi<TData>, range: GridCellRangeSelection) => {
	const columns = api.getAllDisplayedColumns(); //보이는 컬럼들 정보 얻기
	const anchorColumnIndex = columns.findIndex(column => column.getColId() === range.anchor.colId);
	const selectedColumnIndex = columns.findIndex(column => column.getColId() === range.selected.colId);

	if (anchorColumnIndex < 0 || selectedColumnIndex < 0) return null;

	return {
		columns,
		startRowIndex: Math.min(range.anchor.rowIndex, range.selected.rowIndex), //행 범위
		endRowIndex: Math.max(range.anchor.rowIndex, range.selected.rowIndex),
		startColumnIndex: Math.min(anchorColumnIndex, selectedColumnIndex), // 열 범위
		endColumnIndex: Math.max(anchorColumnIndex, selectedColumnIndex),
	};
};

/**
 * AG Grid에서 읽은 원본 셀 값을 계산 가능한 유한 숫자로 변환합니다.
 * number는 NaN과 Infinity를 제외하고 그대로 사용합니다. string은 앞뒤 공백과
 * 모든 쉼표 문자를 제거한 후 Number로 변환하며, 빈 문자열과 변환 실패 값은
 * null을 반환합니다. null, undefined, boolean, 객체 등 그 외 타입은 제외합니다.
 */
const toCalculableNumber = (value: unknown): number | null => {
	if (typeof value === 'number') return Number.isFinite(value) ? value : null;
	if (typeof value !== 'string') return null;

	const normalizedValue = value.trim().replaceAll(',', '');
	if (normalizedValue.length === 0) return null;

	const numericValue = Number(normalizedValue);
	return Number.isFinite(numericValue) ? numericValue : null;
};

/**
 * 우클릭한 셀이 현재 드래그 선택 사각형 안에 포함되는지 확인합니다.
 * 선택 범위 밖에서 우클릭했을 때 이전 선택 범위의 계산 메뉴가 잘못 노출되는 것을
 * 막기 위한 검사이며, 행과 현재 표시 컬럼 위치가 모두 범위 안이어야 true입니다.
 */
export const isCellInSelectedRange = <TData>(
	api: GridApi<TData>,
	range: GridCellRangeSelection,
	rowIndex: number,
	colId: string,
): boolean => {
	const bounds = getRangeBounds(api, range);
	if (!bounds) return false;

	const columnIndex = bounds.columns.findIndex(column => column.getColId() === colId);
	return rowIndex >= bounds.startRowIndex && rowIndex <= bounds.endRowIndex
		&& columnIndex >= bounds.startColumnIndex && columnIndex <= bounds.endColumnIndex;
};

/**
 * 선택 사각형에 포함된 셀 중 계산이 허용된 컬럼의 숫자만 하나의 배열로 수집합니다.
 *
 * 처리 기준:
 * 1. 선택 영역에 포함된 모든 컬럼을 계산 대상으로 확인합니다.
 * 2. 정렬·필터가 반영된 표시 행을 위에서 아래로, 허용 컬럼을 좌에서 우로 순회합니다.
 * 3. 합계용 하단 행과 같은 pinned 행은 실제 데이터가 아니므로 제외합니다.
 * 4. formatter가 적용된 화면 문자열이 아닌 원본 셀 값을 읽습니다.
 * 5. 숫자로 변환할 수 없는 값은 제외하고 변환된 모든 숫자를 같은 배열에 추가합니다.
 */
export const collectSelectedNumericValues = <TData>(
	api: GridApi<TData>,
	range: GridCellRangeSelection,
): number[] => {
	const bounds = getRangeBounds(api, range);
	if (!bounds) return [];

	const selectedColumns = bounds.columns.slice(bounds.startColumnIndex, bounds.endColumnIndex + 1);
	const values: number[] = [];

	for (let rowIndex = bounds.startRowIndex; rowIndex <= bounds.endRowIndex; rowIndex++) {
		const rowNode = api.getDisplayedRowAtIndex(rowIndex);
		if (!rowNode || rowNode.rowPinned) continue;

		selectedColumns.forEach(column => {
			// valueFormatter 결과가 아닌 데이터 원본을 사용해야 표시 형식과 계산 결과가 분리됩니다.
			const value = api.getCellValue({ rowNode, colKey: column.getColId(), useFormatter: false });
			const numericValue = toCalculableNumber(value);
			if (numericValue !== null) values.push(numericValue);
		});
	}

	return values;
};

/**
 * 수집된 숫자 배열 전체를 컬럼 구분 없이 한 번에 집계합니다.
 * 빈 배열은 평균·최솟값·최댓값을 정의할 수 없으므로 null을 반환하며,
 * count는 선택한 셀 수가 아니라 숫자 변환에 성공하여 배열에 포함된 값의 수입니다.
 */
export const summarizeSelectedNumericValues = (
	values: readonly number[],
): GridCellCalculationSummary | null => {
	if (values.length === 0) return null;

	const sum = values.reduce((total, value) => total + value, 0);
	return {
		count: values.length,
		sum,
		average: sum / values.length,
		min: values.reduce((minimum, value) => Math.min(minimum, value), values[0]),
		max: values.reduce((maximum, value) => Math.max(maximum, value), values[0]),
	};
};
